import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import Login from './features/auth/Login';
import OTPPage from './features/auth/OTPPage';
import Dashboard from './features/Dashboard';
import MainLayout from './components/layout/MainLayout';
import ProductsPage from './features/products/ProductsPage';
import OrdersPage from './features/orders/OrdersPage';
import ProfilePage from './features/profile/ProfilePage';
import ProductsManagementPage from './features/admin/products/ProductsManagementPage';
import ProductCreatePage from './features/admin/products/ProductCreatePage';
import ProductEditPage from './features/admin/products/ProductEditPage';
import OrdersManagementPage from './features/admin/OrdersManagementPage';
import UsersManagementPage from './features/admin/users/UsersManagementPage';
import UserCreatePage from './features/admin/users/UserCreatePage';
import UserEditPage from './features/admin/users/UserEditPage';
import ContactUsManagementPage from './features/admin/contact-us/ContactUsManagementPage';
import SliderManagementPage from './features/admin/sliders/SliderManagementPage';
import SliderFormPage from './features/admin/sliders/SliderFormPage';
import LicensesManagementPage from './features/admin/licenses/LicensesManagementPage';
import LicenseFormPage from './features/admin/licenses/LicenseFormPage';
import SiteContentManagementPage from './features/admin/site-content/SiteContentManagementPage';
import { SelectionsManagementPage } from './features/admin/selections/SelectionsManagementPage';
import SelectionsPage from './features/selections/SelectionsPage';
import SelectionDetailsPage from './features/selections/SelectionDetailsPage';
import SelectionSharePage from './features/selections/SelectionSharePage';
import config from './config';
import { Toaster } from 'react-hot-toast';

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen bg-[--color-primary] flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-white/70">در حال بارگذاری...</p>
    </div>
  </div>
);

// Protected route wrapper
const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, checkAuth } = useAuthStore();

  if (!checkAuth() && !isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

// Public route wrapper (redirect to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, checkAuth } = useAuthStore();

  if (checkAuth() || isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Toaster position="top-center" reverseOrder={false} />
      <Router basename={config.app.baseUrl}>
        <Routes>
          {/* Public routes */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/otp"
            element={
              <PublicRoute>
                <OTPPage />
              </PublicRoute>
            }
          />

          <Route
            path="/share/selection/:id"
            element={<SelectionSharePage />}
          />

          {/* Protected routes with layout */}
          <Route element={<ProtectedRoute />}>
            <Route element={<MainLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/selections" element={<SelectionsPage />} />
              <Route path="/selections/:id" element={<SelectionDetailsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/admin/products" element={<ProductsManagementPage />} />
              <Route path="/admin/products/new" element={<ProductCreatePage />} />
              <Route path="/admin/products/:id/edit" element={<ProductEditPage />} />
              <Route path="/admin/orders" element={<OrdersManagementPage />} />
              <Route path="/admin/users" element={<UsersManagementPage />} />
              <Route path="/admin/users/new" element={<UserCreatePage />} />
              <Route path="/admin/users/:id/edit" element={<UserEditPage />} />
              <Route path="/admin/contact-us" element={<ContactUsManagementPage />} />
              <Route path="/admin/sliders" element={<SliderManagementPage />} />
              <Route path="/admin/sliders/new" element={<SliderFormPage />} />
              <Route path="/admin/sliders/:id/edit" element={<SliderFormPage />} />
              <Route path="/admin/licenses" element={<LicensesManagementPage />} />
              <Route path="/admin/licenses/new" element={<LicenseFormPage />} />
              <Route path="/admin/licenses/:id/edit" element={<LicenseFormPage />} />
              <Route path="/admin/site-content" element={<SiteContentManagementPage />} />
              <Route path="/admin/selections" element={<SelectionsManagementPage />} />
            </Route>
          </Route>

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </Suspense>
  );
}

export default App;
